import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  Bar,
} from "recharts";
// Via the api-schemas subpath, not the package root: the root barrel re-exports the drizzle schema
// and its Node-only deps, which is why every other import from common in web is type-only.
import { FEE_BUCKETED_POINT_LIMIT, FEE_MAX_BUCKET_SIZE } from "@clustec/common/api-schemas";
import { theme } from "../lib/theme";
import { noAnimation } from "../lib/chart";
import { useNetworkStore } from "../stores/network";
import { useFeeHistory, useFeeSpread, useCurrentFees, useManaStats } from "../api/hooks";
import { useChartCatchUp } from "../hooks/useChartCatchUp";
import type { FeeHistoryPoint, FeeSpreadBucket } from "../lib/api";
import {
  PageContainer,
  PageTitle,
  StatValue,
  StatLabel,
  Loading,
  PageHeader,
  ChartTitle,
  ChartCard,
  StatsRow,
  CompactStatCard,
  SubStat,
} from "../components/ui";

// All ranges are multiples of the Aztec epoch size (32 slots) so checkpoint
// boundaries land cleanly on the chart edges.
type TimeRange = "32" | "96" | "512" | "1024" | "5120" | "all";

// The counts alone; "blocks" is the toolbar's `unit`, which writes it once per button on a wide
// screen and leaves it off a phone.
const RANGE_LABELS: Record<TimeRange, string> = {
  "32": "32",
  "96": "96",
  "512": "512",
  "1024": "1K",
  "5120": "5K",
  all: "All",
};

// Round numbers, so the block range in each tooltip stays readable, and none wider than the server
// will honour. 100 is the floor: the finest bucket the "All" view ever used.
const ALL_RANGE_BUCKETS = [100, 200, 500, 1000, 2000, 5000, FEE_MAX_BUCKET_SIZE] as const;

/**
 * Bucket width for "All", the one range whose span grows with the chain.
 *
 * Every other range is a fixed block count, so a fixed bucket serves it forever. "All" is not, and a
 * hardcoded 100 blocks per bucket quietly stopped covering it: past FEE_BUCKETED_POINT_LIMIT × 100
 * blocks of history the reads return a capped prefix of the window, which the chart draws as a
 * base-fee line ending partway across. Widening the bucket keeps the point count under the cap, and
 * only ever coarsens the view that would otherwise have been truncated.
 */
export function allRangeBucket(latestBlock: number | null): number {
  if (latestBlock == null) return ALL_RANGE_BUCKETS[0];
  const needed = latestBlock / FEE_BUCKETED_POINT_LIMIT;
  return (
    ALL_RANGE_BUCKETS.find((size) => size >= needed) ??
    ALL_RANGE_BUCKETS[ALL_RANGE_BUCKETS.length - 1]
  );
}

export function resolutionForRange(range: TimeRange, latestBlock: number | null): string {
  switch (range) {
    case "32":
      return "raw";
    case "96":
      return "raw";
    case "512":
      return "raw";
    case "1024":
      return "10";
    case "5120":
      return "50";
    case "all":
      return String(allRangeBucket(latestBlock));
  }
}

/**
 * Mobile chart set. `perMana` is the default rather than the first entry: the distribution strip is
 * context for the two fee charts, and landing on it alone answers nothing a reader came here for.
 */
const MOBILE_CHARTS = [
  { key: "mana", label: "Mana/tx" },
  { key: "perMana", label: "Fee per mana" },
  { key: "total", label: "Total fee" },
] as const;

// Kept in step with resolutionForRange: the merge below bins history rows into spread buckets by
// block range, so a history bucket wider than its spread bucket would leave spread buckets with no
// base fee at all — the same gap, arrived at from the other side.
export function spreadBucketForRange(range: TimeRange, latestBlock: number | null): number {
  switch (range) {
    case "32":
      return 2;
    case "96":
      return 5;
    case "512":
      return 10;
    case "1024":
      return 20;
    case "5120":
      return 50;
    case "all":
      return allRangeBucket(latestBlock);
  }
}

/**
 * The bucket grid for the visible window: a spread bucket for /fees/spread and a resolution for
 * /fees/history. `zoomSpan` is the zoomed width in blocks, or null when the user hasn't zoomed —
 * and null is load-bearing. The fixed ranges have hand-tuned sizes above; passing their window
 * width as a span applies this coarser heuristic instead, and that shadowing (the span used to be
 * computed whenever from/to were known, which is every fixed range) is how the 32-block view came
 * to draw ~6 buckets.
 */
export function chartBuckets(
  range: TimeRange,
  latestBlock: number | null,
  zoomSpan: number | null,
): { spreadBucket: number; resolution: string } {
  if (zoomSpan == null) {
    return {
      spreadBucket: spreadBucketForRange(range, latestBlock),
      resolution: resolutionForRange(range, latestBlock),
    };
  }
  const spreadBucket =
    zoomSpan <= 20
      ? 2
      : zoomSpan <= 100
        ? 5
        : zoomSpan <= 500
          ? 10
          : zoomSpan <= 1000
            ? 20
            : zoomSpan <= 5000
              ? 50
              : 100;
  let resolution: string;
  if (zoomSpan <= 500) resolution = "raw";
  else if (zoomSpan <= 1000) resolution = "10";
  else if (zoomSpan <= 5000) resolution = "50";
  else {
    // The range's own resolution — but never a history bucket wider than the spread bucket it
    // merges into. "All" zoomed on a chain tall enough that allRangeBucket outgrew the 100-block
    // spread bucket used at this span would otherwise leave spread buckets with no base fee.
    const fallback = resolutionForRange(range, latestBlock);
    const parsed = parseInt(fallback, 10);
    resolution = Number.isFinite(parsed) && parsed > spreadBucket ? String(spreadBucket) : fallback;
  }
  return { spreadBucket, resolution };
}

import {
  formatFJ,
  formatFJCompact,
  formatPerManaCompact,
  formatFJPerMana,
} from "../lib/format";
import { RangeToolbar } from "../components/RangeToolbar";
import { ChartPicker, useChartPicker } from "../components/ChartPicker";
import { LiveToggle } from "../components/LiveToggle";

function toNum(v: string | null | undefined): number | null {
  if (!v) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}

/** Convert raw Fee Juice amount to USD via ethPerFeeAsset and ethUsdPrice */
function feeToUsd(
  rawFeeJuice: number,
  ethPerFeeAssetE12: string,
  ethUsdPrice: number,
): number {
  const ethPerFeeAsset = Number(ethPerFeeAssetE12) / 1e12;
  const feeJuiceInEth = (rawFeeJuice / 1e18) * ethPerFeeAsset;
  return feeJuiceInEth * ethUsdPrice;
}

function formatUsd(v: number): string {
  if (v >= 0.01) return `$${v.toFixed(4)}`;
  if (v >= 0.000001) return `$${v.toFixed(8)}`;
  return `$${v.toExponential(3)}`;
}

function formatMana(v: number | undefined): string {
  if (v == null) return "-";
  return Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(v);
}

/**
 * Percentile strip for the mana-per-tx distribution: a recessive min→max
 * track, an emphasized p10→p95 band, a solid median tick and a hollow mean
 * marker on a log scale (the distribution is heavily right-skewed — a linear
 * scale collapses everything left of p95 into a sliver). Single measure, so a
 * single hue; values are direct-labeled below with collision skipping (p50
 * always wins), and every marker carries a native tooltip.
 */
function ManaDistributionStrip({
  stats,
}: {
  stats: { min: number; p10: number; mean: number; p50: number; p95: number; max: number };
}) {
  // Label collision must be resolved in pixels — a percent threshold passes on
  // desktop and overlaps on mobile, where the same percent gap is fewer px
  // than the label text. Measure the rendered width and estimate text extents.
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setWidth(entries[0]?.contentRect.width ?? 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const PAD = 3; // percent inset so end labels/markers don't clip
  const lo = Math.max(stats.min, 1);
  const hi = Math.max(stats.max, lo);
  const span = Math.log10(hi) - Math.log10(lo);
  const xPct = (v: number) =>
    span <= 0 ? 50 : PAD + ((Math.log10(Math.max(v, lo)) - Math.log10(lo)) / span) * (100 - 2 * PAD);

  // Two label rows so all six stats stay labeled: band stats (p10/mean/p95)
  // above, extremes + median below. Within each row a label is dropped when
  // its estimated pixel extent would intersect the last kept one — the strong
  // label (median) always wins over its neighbor.
  type LabelEntry = { key: string; label: string; v: number; strong?: boolean; x: number };
  const CHAR_W = 6.6; // ~px per char at 11-12px mono-ish label text
  const GAP = 8;
  const extent = (e: LabelEntry): [number, number] => {
    const w = `${e.label} ${formatMana(e.v)}`.length * CHAR_W;
    const px = (e.x / 100) * width;
    if (e.key === "min") return [px, px + w]; // anchor start
    if (e.key === "max") return [px - w, px]; // anchor end
    return [px - w / 2, px + w / 2];
  };
  const dedupe = (row: LabelEntry[]): LabelEntry[] => {
    if (width === 0) return row; // first paint, corrected on measure
    const visible: LabelEntry[] = [];
    for (const e of row) {
      const last = visible[visible.length - 1];
      if (!last || extent(e)[0] - extent(last)[1] >= GAP) {
        visible.push({ ...e });
      } else if (formatMana(e.v) === formatMana(last.v)) {
        // Same displayed value (spike-shaped distribution): merge the names
        // into one label ("p10·p50 670.1K") instead of silently dropping one —
        // a dropped percentile made the strip look broken when p10≈p50≈p95.
        last.label = `${last.label}·${e.label}`;
        last.strong = last.strong || e.strong;
      } else if (e.strong) {
        visible.pop();
        visible.push({ ...e });
      }
    }
    return visible;
  };
  const mk = (key: string, label: string, v: number, strong?: boolean): LabelEntry => ({
    key, label, v, strong, x: xPct(v),
  });
  const topRow = dedupe(
    [mk("p10", "p10", stats.p10), mk("mean", "mean", stats.mean), mk("p95", "p95", stats.p95)]
      .sort((a, b) => a.x - b.x),
  );
  const bottomRow = dedupe([
    mk("min", "min", stats.min),
    mk("p50", "p50", stats.p50, true),
    mk("max", "max", stats.max),
  ]);

  const bandY = 28;
  const bandH = 16;
  const mid = bandY + bandH / 2;

  return (
    <div ref={containerRef}>
      <svg width="100%" height="82" role="img" aria-label="Mana per transaction distribution">
      {/* full-range track */}
      <line
        x1={`${xPct(stats.min)}%`}
        x2={`${xPct(stats.max)}%`}
        y1={mid}
        y2={mid}
        stroke={theme.colors.border}
        strokeWidth={4}
        strokeLinecap="round"
      >
        <title>{`range ${formatMana(stats.min)} – ${formatMana(stats.max)} mana`}</title>
      </line>
      {/* p10–p95 band */}
      <rect
        x={`${xPct(stats.p10)}%`}
        y={bandY}
        width={`${Math.max(xPct(stats.p95) - xPct(stats.p10), 0.5)}%`}
        height={bandH}
        rx={4}
        fill={theme.colors.success}
        fillOpacity={0.22}
        stroke={theme.colors.success}
        strokeOpacity={0.45}
      >
        <title>{`p10–p95: ${formatMana(stats.p10)} – ${formatMana(stats.p95)} mana`}</title>
      </rect>
      {/* median tick */}
      <line
        x1={`${xPct(stats.p50)}%`}
        x2={`${xPct(stats.p50)}%`}
        y1={bandY - 5}
        y2={bandY + bandH + 5}
        stroke={theme.colors.success}
        strokeWidth={3}
        strokeLinecap="round"
      >
        <title>{`median ${formatMana(stats.p50)} mana`}</title>
      </line>
      {/* mean marker (hollow, on a 2px surface ring so it reads over the band) */}
      <circle
        cx={`${xPct(stats.mean)}%`}
        cy={mid}
        r={5}
        fill={theme.colors.bgCard}
        stroke={theme.colors.text}
        strokeWidth={1.5}
      >
        <title>{`mean ${formatMana(stats.mean)} mana`}</title>
      </circle>
      {/* label rows: band stats above, extremes + median below */}
      {topRow.map((e) => (
        <text
          key={e.key}
          x={`${e.x}%`}
          y={14}
          textAnchor="middle"
          fill={theme.colors.textMuted}
          fontSize={11}
        >
          {`${e.label} ${formatMana(e.v)}`}
        </text>
      ))}
      {bottomRow.map((e) => (
        <text
          key={e.key}
          x={`${e.x}%`}
          y={74}
          textAnchor={e.key === "min" ? "start" : e.key === "max" ? "end" : "middle"}
          fill={e.strong ? theme.colors.text : theme.colors.textMuted}
          fontSize={e.strong ? 12 : 11}
          fontWeight={e.strong ? 600 : 400}
        >
          {`${e.label} ${formatMana(e.v)}`}
        </text>
      ))}
      </svg>
    </div>
  );
}

/**
 * Chart legend whose samples show the series' actual stroke: solid/dashed/
 * dotted line, translucent band or bar — so "what are the dashed lines?"
 * is answered by the legend itself.
 */
type LegendKind = "line" | "dash" | "dot" | "band" | "bar";
function ChartLegendRow({ specs }: { specs: { label: string; kind: LegendKind; color: string }[] }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "6px 14px",
        fontSize: 11,
        color: theme.colors.textMuted,
        marginTop: 6,
      }}
    >
      {specs.map((spec) => (
        <span key={spec.label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <svg width="20" height="10" style={{ flexShrink: 0 }}>
            {spec.kind === "band" || spec.kind === "bar" ? (
              <rect
                width="20"
                height="10"
                rx="2"
                fill={spec.color}
                opacity={spec.kind === "band" ? 0.25 : 0.45}
              />
            ) : (
              <line
                x1="0"
                y1="5"
                x2="20"
                y2="5"
                stroke={spec.color}
                strokeWidth={spec.kind === "line" ? 2 : 1.4}
                strokeDasharray={
                  spec.kind === "dash" ? "6 3" : spec.kind === "dot" ? "2 3" : undefined
                }
              />
            )}
          </svg>
          {spec.label}
        </span>
      ))}
    </div>
  );
}

/**
 * Dot renderer that only marks data points whose neighbors are empty —
 * without it, a lone bucket between no-tx gaps is invisible once lines
 * stop interpolating across missing data.
 */
function isolatedDot(data: Record<string, unknown>[], key: string, color: string) {
  return function IsolatedDot(props: { cx?: number; cy?: number; index?: number; value?: unknown }) {
    const { cx, cy, index, value } = props;
    const empty = <g key={`${key}-${index}`} />;
    if (cx == null || cy == null || index == null || value == null) return empty;
    const prev = index > 0 ? data[index - 1]?.[key] : null;
    const next = index < data.length - 1 ? data[index + 1]?.[key] : null;
    if (prev != null || next != null) return empty;
    return <circle key={`${key}-${index}`} cx={cx} cy={cy} r={2.5} fill={color} />;
  };
}

export function Fees() {
  const { selectedNetwork } = useNetworkStore();
  const catchUp = useChartCatchUp();
  const {
    selected: mobileChart,
    setSelected: setMobileChart,
    showChart,
  } = useChartPicker<(typeof MOBILE_CHARTS)[number]["key"]>("perMana");
  const [searchParams, setSearchParams] = useSearchParams();

  const urlFrom = searchParams.get("from")
    ? parseInt(searchParams.get("from")!, 10)
    : null;
  const urlTo = searchParams.get("to")
    ? parseInt(searchParams.get("to")!, 10)
    : null;

  const [range, setRange] = useState<TimeRange>(() => {
    if (urlFrom != null && urlTo != null) {
      const diff = urlTo - urlFrom;
      if (diff <= 32) return "32";
      if (diff <= 96) return "96";
      if (diff <= 512) return "512";
      if (diff <= 1024) return "1024";
      if (diff <= 5120) return "5120";
      return "all";
    }
    return "96";
  });

  // Zoom state: drag-select on any chart to zoom all three
  const [zoomFrom, setZoomFrom] = useState<number | null>(urlFrom);
  const [zoomTo, setZoomTo] = useState<number | null>(urlTo);
  const [selecting, setSelecting] = useState<number | null>(null);
  const [selectEnd, setSelectEnd] = useState<number | null>(null);

  // Refs to avoid stale closures in recharts callbacks
  const selectingRef = useRef<number | null>(null);
  const selectEndRef = useRef<number | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type ChartMouseEvent = any;
  const getBlock = (e: ChartMouseEvent): number | null => {
    if (!e) return null;
    if (e.activeLabel !== undefined && e.activeLabel !== null)
      return Number(e.activeLabel);
    const block = e.activePayload?.[0]?.payload?.block;
    if (block != null) return Number(block);
    return null;
  };
  const handleMouseDown = useCallback((e: ChartMouseEvent) => {
    const block = getBlock(e);
    if (block != null && !isNaN(block)) {
      selectingRef.current = block;
      selectEndRef.current = null;
      setSelecting(block);
      setSelectEnd(null);
    }
  }, []);
  const handleMouseMove = useCallback((e: ChartMouseEvent) => {
    if (selectingRef.current == null) return;
    const block = getBlock(e);
    if (block != null && !isNaN(block)) {
      selectEndRef.current = block;
      setSelectEnd(block);
    }
  }, []);
  const handleMouseUp = useCallback(() => {
    const start = selectingRef.current;
    const end = selectEndRef.current;
    if (start != null && end != null && start !== end) {
      setZoomFrom(Math.min(start, end));
      setZoomTo(Math.max(start, end));
    }
    selectingRef.current = null;
    selectEndRef.current = null;
    setSelecting(null);
    setSelectEnd(null);
  }, []);
  const resetZoom = useCallback(() => {
    setZoomFrom(null);
    setZoomTo(null);
  }, []);

  const [offset, setOffset] = useState(0);

  const { data: currentData } = useCurrentFees(selectedNetwork);
  // Compute from/to block range based on latest block
  const latestBlock = currentData?.block?.blockNumber ?? null;
  const rangeBlocks = range === "all" ? null : parseInt(range, 10);
  const windowEnd =
    latestBlock != null && rangeBlocks != null
      ? latestBlock - offset * rangeBlocks
      : null;
  const fromBlock =
    windowEnd != null
      ? Math.max(0, windowEnd - rangeBlocks!)
      : range === "all"
        ? undefined
        : undefined;
  const toBlock = range !== "all" ? (windowEnd ?? undefined) : undefined;
  const isZoomed = zoomFrom != null || zoomTo != null;
  const canGoBack =
    range !== "all" &&
    (isZoomed ? (zoomFrom ?? 0) > 0 : fromBlock != null && fromBlock > 0);
  const canGoForward =
    range !== "all" &&
    (isZoomed ? (zoomTo ?? 0) < (latestBlock ?? 0) : offset > 0);

  const goBack = () => {
    if (!canGoBack) return;
    if (isZoomed && zoomFrom != null && zoomTo != null) {
      const span = zoomTo - zoomFrom;
      setZoomFrom(Math.max(0, zoomFrom - span));
      setZoomTo(Math.max(span, zoomTo - span));
    } else {
      setOffset((o) => o + 1);
    }
  };
  const goForward = () => {
    if (!canGoForward) return;
    if (isZoomed && zoomFrom != null && zoomTo != null) {
      const span = zoomTo - zoomFrom;
      setZoomFrom(zoomFrom + span);
      setZoomTo(zoomTo + span);
    } else {
      setOffset((o) => o - 1);
    }
  };

  // Sync URL with visible range + network
  const effectiveFrom = zoomFrom ?? fromBlock;
  const effectiveTo = zoomTo ?? toBlock;

  // Mana distribution follows the visited range (incl. zoom); "all" without
  // zoom falls back to the trailing 1,000 blocks.
  const manaRangeReady = range === "all" || fromBlock != null || isZoomed;
  const { data: manaStats } = useManaStats(
    manaRangeReady ? selectedNetwork : "",
    effectiveFrom != null || effectiveTo != null
      ? { from: effectiveFrom, to: effectiveTo }
      : { blocks: 1000 },
  );
  useEffect(() => {
    if (effectiveFrom != null && effectiveTo != null) {
      const next = new URLSearchParams(searchParams);
      next.set("from", String(effectiveFrom));
      next.set("to", String(effectiveTo));
      setSearchParams(next, { replace: true });
    } else if (range === "all") {
      const next = new URLSearchParams(searchParams);
      next.delete("from");
      next.delete("to");
      setSearchParams(next, { replace: true });
    }
  }, [effectiveFrom, effectiveTo, range, selectedNetwork, setSearchParams]);

  // For queries, use the effective (zoomed) range when available
  const queryFrom = effectiveFrom;
  const queryTo = effectiveTo;

  // Zoom is the one view with an arbitrary span; unzoomed, the fixed ranges keep their hand-tuned
  // sizes and "All" sizes its buckets against chain height (see chartBuckets).
  const zoomSpan =
    isZoomed && queryFrom != null && queryTo != null ? queryTo - queryFrom : null;
  const { spreadBucket, resolution: effectiveResolution } = chartBuckets(
    range,
    latestBlock,
    zoomSpan,
  );

  const historyOpts = useMemo(
    () => ({ from: queryFrom ?? undefined, to: queryTo ?? undefined, resolution: effectiveResolution }),
    [queryFrom, queryTo, effectiveResolution],
  );

  const spreadOpts = useMemo(
    () => ({
      from: queryFrom ?? undefined,
      to: queryTo ?? undefined,
      bucketSize: spreadBucket,
    }),
    [queryFrom, queryTo, spreadBucket],
  );

  const rangeReady = range === "all" || fromBlock != null || isZoomed;
  const { data: historyData, isLoading: historyLoading } = useFeeHistory(
    rangeReady ? selectedNetwork : "",
    historyOpts,
  );
  const { data: spreadData, isLoading: spreadLoading } = useFeeSpread(
    rangeReady ? selectedNetwork : "",
    spreadOpts,
  );

  // Build padded chart data covering the full selected range
  const { priceChartData, costChartData, xDomain, xTicks } = useMemo(() => {
    const start = queryFrom ?? 0;
    const end = queryTo ?? latestBlock ?? start;

    // Index history data by block number (coerce to number — SQL may return strings)
    const historyByBlock = new Map<number, FeeHistoryPoint>();
    for (const p of historyData?.data ?? []) {
      historyByBlock.set(Number(p.blockNumber), p);
    }

    // Index spread data by bucket
    const spreadByBucket = new Map<number, FeeSpreadBucket>();
    for (const b of spreadData?.data ?? []) {
      spreadByBucket.set(Number(b.bucket), b);
    }

    // Chart 1: Fee Per Mana — base fee + tx bid spread, per spread bucket
    // We merge block base fees (averaged over the bucket) with tx bid percentiles
    const priceRows: {
      block: number;
      blockEnd: number;
      baseDa: number | null;
      baseL2: number | null;
      medianBidL2: number | null;
      p25BidL2: number | null;
      p75BidL2: number | null;
      medianBidDa: number | null;
      pricing: FeeSpreadBucket["pricing"];
    }[] = [];

    // Chart 2: Total Fee Paid + utilization
    const costRows: {
      block: number;
      blockEnd: number;
      medianFee: number | null;
      p25Fee: number | null;
      p75Fee: number | null;
      minFee: number | null;
      maxFee: number | null;
      txCount: number;
      pricing: FeeSpreadBucket["pricing"];
    }[] = [];

    // When history is bucketed, each row key is a bucket start (e.g. 15500, 15510).
    // Collect all history rows whose key falls within each spread bucket range.
    const historyKeys = [...historyByBlock.keys()].sort((a, b) => a - b);

    const startSpread = Math.floor(start / spreadBucket) * spreadBucket;
    for (let b = startSpread; b <= end; b += spreadBucket) {
      const s = spreadByBucket.get(b);

      // Average block base fees from all history entries within [b, b+spreadBucket)
      let sumDa = 0,
        sumL2 = 0,
        countFee = 0;
      for (const hk of historyKeys) {
        if (hk < b) continue;
        if (hk >= b + spreadBucket) break;
        const h = historyByBlock.get(hk)!;
        if (h.feePerDaGas != null && h.feePerL2Gas != null) {
          sumDa += Number(h.feePerDaGas);
          sumL2 += Number(h.feePerL2Gas);
          countFee++;
        }
      }

      priceRows.push({
        block: b,
        blockEnd: Math.min(b + spreadBucket - 1, end),
        baseDa: countFee > 0 ? sumDa / countFee : null,
        baseL2: countFee > 0 ? sumL2 / countFee : null,
        medianBidL2: s ? toNum(s.medianMaxFeePerL2Gas) : null,
        p25BidL2: s ? toNum(s.p25MaxFeePerL2Gas) : null,
        p75BidL2: s ? toNum(s.p75MaxFeePerL2Gas) : null,
        medianBidDa: s ? toNum(s.medianMaxFeePerDaGas) : null,
        pricing: s?.pricing,
      });

      // Aggregate tx count from history entries in this bucket
      let txs = 0;
      for (const hk of historyKeys) {
        if (hk < b) continue;
        if (hk >= b + spreadBucket) break;
        const h = historyByBlock.get(hk)!;
        txs += h.numTxs ?? 0;
      }

      costRows.push({
        block: b,
        blockEnd: Math.min(b + spreadBucket - 1, end),
        medianFee: s ? toNum(s.medianActualFee) : null,
        p25Fee: s ? toNum(s.p25ActualFee) : null,
        p75Fee: s ? toNum(s.p75ActualFee) : null,
        minFee: s ? toNum(s.minActualFee) : null,
        maxFee: s ? toNum(s.maxActualFee) : null,
        txCount: s ? Number(s.txCount) : txs,
        pricing: s?.pricing,
      });
    }

    // Apply zoom filter
    const inZoom = (block: number) =>
      (zoomFrom == null || block >= zoomFrom) &&
      (zoomTo == null || block <= zoomTo);

    const visiblePrice = priceRows.filter((r) => inZoom(r.block));
    const visibleCost = costRows.filter((r) => inZoom(r.block));

    // Pad the numeric x-domain by half a bucket so bars centered on bucket
    // starts don't overflow the plot edges (same fix as the Blocks charts),
    // with ticks snapped to whole block numbers.
    let xDomain: [number, number] = [0, 0];
    const xTicks: number[] = [];
    if (visibleCost.length > 0) {
      const min = visibleCost[0].block;
      const max = visibleCost[visibleCost.length - 1].block;
      xDomain = [min - spreadBucket / 2, max + spreadBucket / 2];
      const span = Math.max(1, max - min);
      const rough = Math.max(1, span / 6);
      const pow = Math.pow(10, Math.floor(Math.log10(rough)));
      const nice = [1, 2, 5, 10].find((m) => m * pow >= rough) ?? 10;
      const step = Math.max(1, nice * pow);
      const start = Math.ceil(min / step) * step;
      for (let v = start; v <= max; v += step) xTicks.push(v);
    }

    return {
      priceChartData: visiblePrice,
      costChartData: visibleCost,
      xDomain,
      xTicks,
    };
  }, [
    historyData,
    spreadData,
    queryFrom,
    queryTo,
    latestBlock,
    spreadBucket,
    zoomFrom,
    zoomTo,
  ]);

  // Shared drag-to-zoom props for all ComposedCharts
  const zoomProps = {
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
  };

  // Selection overlay element (rendered inside each chart when dragging)
  const selectionOverlay =
    selecting != null && selectEnd != null ? (
      <ReferenceArea
        x1={Math.min(selecting, selectEnd)}
        x2={Math.max(selecting, selectEnd)}
        fill={theme.colors.primary}
        fillOpacity={0.15}
        stroke={theme.colors.primary}
        strokeOpacity={0.4}
        ifOverflow="extendDomain"
        yAxisId="fee"
      />
    ) : null;

  // Current stats
  const currentDaFee = toNum(currentData?.block?.feePerDaGas);
  const currentL2Fee = toNum(currentData?.block?.feePerL2Gas);
  const ethPrice = currentData?.pricing?.ethUsdPrice ?? null;
  const ethPerFeeAssetE12 = currentData?.pricing?.ethPerFeeAssetE12 ?? null;

  return (
    <FeesContainer>
      <PageHeader>
        <PageTitle>Fee Analytics</PageTitle>
        <HeaderControls>
          <LiveToggle knownHead={latestBlock} />
          <RangeToolbar
            unit="blocks"
            ranges={Object.entries(RANGE_LABELS).map(([key, label]) => ({
              key,
              label,
              mobileHidden: key === "1024" || key === "5120" || key === "all",
            }))}
            activeRange={(() => {
              const zoomSize =
                isZoomed && zoomFrom != null && zoomTo != null
                  ? zoomTo - zoomFrom
                  : null;
              if (zoomSize != null) {
                const match = Object.keys(RANGE_LABELS).find(
                  (r) => r !== "all" && parseInt(r, 10) === zoomSize,
                );
                return match ?? null;
              }
              return range;
            })()}
            onRangeSelect={(r) => {
              const newRange = r as TimeRange;
              if (newRange === "all") {
                resetZoom();
                setOffset(0);
              } else {
                const newSize = parseInt(r, 10);
                if (isZoomed && zoomFrom != null && zoomTo != null) {
                  const center = Math.round((zoomFrom + zoomTo) / 2);
                  const newFrom = Math.max(0, center - Math.floor(newSize / 2));
                  setZoomFrom(newFrom);
                  setZoomTo(newFrom + newSize);
                } else {
                  resetZoom();
                  setOffset(0);
                }
              }
              setRange(newRange);
            }}
            canGoBack={canGoBack}
            canGoForward={canGoForward}
            onGoBack={goBack}
            onGoForward={goForward}
            onGoToLatest={() => {
              setOffset(0);
              resetZoom();
              // Refetch too: while the charts are paused this page's idea of the head is itself
              // behind the chain, so resetting the window alone would land on a stale "latest".
              catchUp();
            }}
            showArrows={range !== "all"}
          />
        </HeaderControls>
      </PageHeader>

      {/* Current stats */}
      <StatsRow>
        <CompactStatCard>
          <StatLabel>DA Base Fee</StatLabel>
          <StatValue style={{ color: theme.colors.primary }}>
            {formatFJPerMana(currentDaFee)} <Unit>FJ/mana</Unit>
          </StatValue>
          {ethPrice != null &&
            ethPerFeeAssetE12 != null &&
            currentDaFee != null && (
              <SubStat>
                ~
                {formatUsd(feeToUsd(currentDaFee, ethPerFeeAssetE12, ethPrice))}
                /mana
              </SubStat>
            )}
        </CompactStatCard>
        <CompactStatCard>
          <StatLabel>L2 Base Fee</StatLabel>
          <StatValue style={{ color: theme.colors.accent }}>
            {formatFJPerMana(currentL2Fee)} <Unit>FJ/mana</Unit>
          </StatValue>
          {ethPrice != null &&
            ethPerFeeAssetE12 != null &&
            currentL2Fee != null && (
              <SubStat>
                ~
                {formatUsd(feeToUsd(currentL2Fee, ethPerFeeAssetE12, ethPrice))}
                /mana
              </SubStat>
            )}
        </CompactStatCard>
        <CompactStatCard>
          <StatLabel>ETH / USD</StatLabel>
          <StatValue>
            {ethPrice != null ? `$${ethPrice.toLocaleString()}` : "-"}
          </StatValue>
          {currentData?.block && (
            <SubStat>Block #{currentData.block.blockNumber}</SubStat>
          )}
        </CompactStatCard>
        <CompactStatCard>
          <StatLabel>Mana / Tx (p50)</StatLabel>
          <StatValue style={{ color: theme.colors.success }}>
            {formatMana(manaStats?.manaPerTx?.p50)} <Unit>mana</Unit>
          </StatValue>
          {manaStats?.txCount != null && manaStats.txCount > 0 && (
            <SubStat>
              {manaStats.txCount.toLocaleString()} txs
              {manaStats.blockRange?.from != null && manaStats.blockRange?.to != null
                ? ` · blocks ${manaStats.blockRange.from}–${manaStats.blockRange.to}`
                : ""}
            </SubStat>
          )}
        </CompactStatCard>
      </StatsRow>

      <ChartPicker charts={MOBILE_CHARTS} value={mobileChart} onChange={setMobileChart} />

      {/* Mana per tx distribution strip */}
      {showChart("mana") && manaStats?.manaPerTx && (
        <ChartCard>
          <ChartTitle>Mana per Transaction</ChartTitle>
          <ChartSubtitle>
            Distribution across the blocks in view
            {manaStats.txCount ? ` (${manaStats.txCount.toLocaleString()} txs` : ""}
            {manaStats.blockRange?.from != null && manaStats.blockRange?.to != null
              ? `, blocks ${manaStats.blockRange.from}–${manaStats.blockRange.to})`
              : manaStats.txCount
                ? ")"
                : ""} — derived as actual fee ÷ block base fee. Log scale; band spans p10–p95,
            tick is the median, hollow dot the mean.
          </ChartSubtitle>
          <ManaDistributionStrip stats={manaStats.manaPerTx} />
        </ChartCard>
      )}

      {/* Chart 1: Fee Per Mana — base fee vs tx bids */}
      {showChart("perMana") && (
        <ChartCard>
          <ChartTitle>Fee Per Mana</ChartTitle>
          <ChartSubtitle>
            Block base fee vs what txs bid (Fee Juice per mana). Gaps mean no txs
            in those blocks; isolated buckets show as dots.
          </ChartSubtitle>
          {historyLoading || spreadLoading || !rangeReady ? (
            <Loading />
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <ComposedChart
                data={priceChartData}
                margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
                {...zoomProps}
              >
                <defs>
                  <linearGradient id="gradBidL2" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={theme.colors.accent}
                      stopOpacity={0.2}
                    />
                    <stop
                      offset="100%"
                      stopColor={theme.colors.accent}
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme.colors.border}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="block"
                  type="number"
                  domain={xDomain}
                  ticks={xTicks}
                  stroke={theme.colors.textMuted}
                  fontSize={10}
                  tickFormatter={(v) => `#${v}`}
                />
                <YAxis
                  yAxisId="fee"
                  stroke={theme.colors.textMuted}
                  fontSize={10}
                  tickFormatter={(v) => formatPerManaCompact(v)}
                />
                <Tooltip
                  contentStyle={{
                    background: theme.colors.bgCard,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.radius.sm,
                    fontSize: 11,
                    fontFamily: "monospace",
                  }}
                  labelFormatter={(v, payload) => {
                    const row = payload?.[0]?.payload;
                    if (row?.blockEnd != null && row.blockEnd !== v)
                      return `Blocks #${v}–#${row.blockEnd}`;
                    return `Block #${v}`;
                  }}
                  formatter={(value: number | number[], name: string) => {
                    const labels: Record<string, string> = {
                      baseL2: "Base Fee (L2)",
                      baseDa: "Base Fee (DA)",
                      medianBidL2: "Median Bid (L2)",
                      bandBidL2: "L2 Bid p25\u2013p75",
                      medianBidDa: "Median Bid (DA)",
                    };
                    const text = Array.isArray(value)
                      ? `${formatFJPerMana(value[0])} \u2013 ${formatFJPerMana(value[1])} FJ/mana`
                      : `${formatFJPerMana(value)} FJ/mana`;
                    return [text, labels[name] ?? name];
                  }}
                />
                {/* L2 bid spread band (p25\u2013p75) — a real range area, so it
                    disappears over no-tx gaps instead of masking the grid */}
                <Area
                  yAxisId="fee"
                  type="monotone"
                  dataKey={(d: { p25BidL2: number | null; p75BidL2: number | null }) =>
                    d.p25BidL2 != null && d.p75BidL2 != null ? [d.p25BidL2, d.p75BidL2] : []
                  }
                  stroke="none"
                  fill="url(#gradBidL2)"
                  name="bandBidL2"
                  {...noAnimation}
                />
                {/* Base fees */}
                <Line
                  yAxisId="fee"
                  type="monotone"
                  dataKey="baseL2"
                  stroke={theme.colors.accent}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  name="baseL2"
                  {...noAnimation}
                />
                <Line
                  yAxisId="fee"
                  type="monotone"
                  dataKey="baseDa"
                  stroke={theme.colors.primary}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  name="baseDa"
                  {...noAnimation}
                />
                {/* Median bids — dashed; gaps stay gaps */}
                <Line
                  yAxisId="fee"
                  type="monotone"
                  dataKey="medianBidL2"
                  stroke={theme.colors.accent}
                  strokeWidth={1}
                  strokeDasharray="6 3"
                  dot={isolatedDot(priceChartData, "medianBidL2", theme.colors.accent)}
                  name="medianBidL2"
                  {...noAnimation}
                />
                <Line
                  yAxisId="fee"
                  type="monotone"
                  dataKey="medianBidDa"
                  stroke={theme.colors.primary}
                  strokeWidth={1}
                  strokeDasharray="6 3"
                  dot={isolatedDot(priceChartData, "medianBidDa", theme.colors.primary)}
                  name="medianBidDa"
                  {...noAnimation}
                />
                {selectionOverlay}
              </ComposedChart>
            </ResponsiveContainer>
          )}
          <ChartLegendRow
            specs={[
              { label: "L2 Base Fee", kind: "line", color: theme.colors.accent },
              { label: "DA Base Fee", kind: "line", color: theme.colors.primary },
              { label: "Median L2 Bid", kind: "dash", color: theme.colors.accent },
              { label: "Median DA Bid", kind: "dash", color: theme.colors.primary },
              { label: "L2 Bid p25\u2013p75", kind: "band", color: theme.colors.accent },
            ]}
          />
        </ChartCard>
      )}

      {/* Chart 2: Total Fee Paid + utilization */}
      {showChart("total") && (
        <ChartCard>
          <ChartTitle>Total Fee Paid</ChartTitle>
          <ChartSubtitle>
            Actual tx fee distribution (Fee Juice). Gaps mean no txs in those
            blocks; isolated buckets show as dots.
          </ChartSubtitle>
          {historyLoading || spreadLoading || !rangeReady ? (
            <Loading />
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart
                data={costChartData}
                margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
                {...zoomProps}
              >
                <defs>
                  <linearGradient id="gradCost" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={theme.colors.success}
                      stopOpacity={0.25}
                    />
                    <stop
                      offset="100%"
                      stopColor={theme.colors.success}
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme.colors.border}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="block"
                  type="number"
                  domain={xDomain}
                  ticks={xTicks}
                  stroke={theme.colors.textMuted}
                  fontSize={10}
                  tickFormatter={(v) => `#${v}`}
                />
                <YAxis
                  yAxisId="fee"
                  stroke={theme.colors.textMuted}
                  fontSize={10}
                  tickFormatter={(v) => formatFJCompact(v)}
                />
                <YAxis
                  yAxisId="txs"
                  orientation="right"
                  stroke={theme.colors.textMuted}
                  fontSize={10}
                />
                <Tooltip
                  contentStyle={{
                    background: theme.colors.bgCard,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.radius.sm,
                    fontSize: 11,
                    fontFamily: "monospace",
                  }}
                  labelFormatter={(v, payload) => {
                    const row = payload?.[0]?.payload;
                    const stale =
                      row?.pricing != null &&
                      row.pricing.snapshotAgeSeconds > 10800
                        ? ` ⚠ stale pricing (${Math.round(row.pricing.snapshotAgeSeconds / 3600)}h old)`
                        : "";
                    if (row?.blockEnd != null && row.blockEnd !== v)
                      return `Blocks #${v}–#${row.blockEnd}${stale}`;
                    return `Block #${v}${stale}`;
                  }}
                  formatter={(value, name, item) => {
                    if (name === "txCount") return [`${value}`, "Txs"];
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const p = (item as any)?.payload?.pricing as { ethUsdPrice: number; ethPerFeeAssetE12: string; snapshotAgeSeconds: number } | null | undefined;
                    const labels: Record<string, string> = {
                      medianFee: "Median",
                      bandFee: "p25\u2013p75",
                      minFee: "Min",
                      maxFee: "Max",
                    };
                    if (Array.isArray(value)) {
                      return [
                        `${formatFJ(Number(value[0]))} \u2013 ${formatFJ(Number(value[1]))}`,
                        labels[String(name)] ?? name,
                      ];
                    }
                    const fj = formatFJ(Number(value));
                    const usd =
                      p != null
                        ? ` (${formatUsd(feeToUsd(Number(value), p.ethPerFeeAssetE12, p.ethUsdPrice))})`
                        : ethPrice != null && ethPerFeeAssetE12 != null
                          ? ` (${formatUsd(feeToUsd(Number(value), ethPerFeeAssetE12, ethPrice))})`
                          : "";
                    return [`${fj}${usd}`, labels[String(name)] ?? name];
                  }}
                />
                {/* Tx count as subtle area on right axis */}
                <Bar
                  yAxisId="txs"
                  dataKey="txCount"
                  fill={theme.colors.primary}
                  fillOpacity={0.2}
                  stroke={theme.colors.primary}
                  strokeOpacity={0.4}
                  strokeWidth={0.5}
                  name="txCount"
                  {...noAnimation}
                />
                {/* Min-max whiskers — dotted; gaps stay gaps */}
                <Line
                  yAxisId="fee"
                  type="monotone"
                  dataKey="maxFee"
                  stroke={theme.colors.warning}
                  strokeWidth={0.8}
                  strokeDasharray="2 3"
                  dot={isolatedDot(costChartData, "maxFee", theme.colors.warning)}
                  name="maxFee"
                  {...noAnimation}
                />
                <Line
                  yAxisId="fee"
                  type="monotone"
                  dataKey="minFee"
                  stroke={theme.colors.accent}
                  strokeWidth={0.8}
                  strokeDasharray="2 3"
                  dot={isolatedDot(costChartData, "minFee", theme.colors.accent)}
                  name="minFee"
                  {...noAnimation}
                />
                {/* p25\u2013p75 band as a real range area */}
                <Area
                  yAxisId="fee"
                  type="monotone"
                  dataKey={(d: { p25Fee: number | null; p75Fee: number | null }) =>
                    d.p25Fee != null && d.p75Fee != null ? [d.p25Fee, d.p75Fee] : []
                  }
                  stroke="none"
                  fill="url(#gradCost)"
                  name="bandFee"
                  {...noAnimation}
                />
                {/* Median line */}
                <Line
                  yAxisId="fee"
                  type="monotone"
                  dataKey="medianFee"
                  stroke={theme.colors.success}
                  strokeWidth={2}
                  dot={isolatedDot(costChartData, "medianFee", theme.colors.success)}
                  name="medianFee"
                  {...noAnimation}
                />
                {selectionOverlay}
              </ComposedChart>
            </ResponsiveContainer>
          )}
          <ChartLegendRow
            specs={[
              { label: "Median Fee", kind: "line", color: theme.colors.success },
              { label: "Fee p25\u2013p75", kind: "band", color: theme.colors.success },
              { label: "Min", kind: "dot", color: theme.colors.accent },
              { label: "Max", kind: "dot", color: theme.colors.warning },
              { label: "Txs per Bucket", kind: "bar", color: theme.colors.primary },
            ]}
          />
        </ChartCard>
      )}
    </FeesContainer>
  );
}

// ── Styled ──

const FeesContainer = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};

  @media (max-width: 768px) {
    gap: ${theme.spacing.sm};
  }
`;

/** Live switch + range toolbar as one group, so they wrap together away from the page title. */
const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  /* Lets the row narrow past its widest child so the range toolbar inside can scroll instead of
     handing this box a width the phone does not have. See RangeToolbar's Controls. */
  min-width: 0;
`;

const ChartSubtitle = styled.p`
  margin: 0 0 ${theme.spacing.md};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const Unit = styled.span`
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.textMuted};
  font-weight: 400;
`;

